import { createContext, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "../hooks/useAuth";
import { useLocation } from "react-router-dom";
import { getAccessToken } from "../utils/sessionMethods";
import { toast } from "react-toastify";

export const SocketContext = createContext({});

const publicRoutes = ["/", "/login", "/register", "/forgot-password", '/e-check', /^\/resgatar-echeck\/[^/]+$/];

export function SocketProvider({ children }) {
  const socketRef = useRef(null);
  const { user, logout } = useAuth();
  const location = useLocation();

  const FORCE_LOGOUT_MESSAGES = {
    token_expired: "Sua sessão expirou. Faça login novamente.",
    new_session: "Sua sessão foi encerrada pois você entrou em outro dispositivo.",
    kicked_by_admin: "Sua sessão foi encerrada pelo administrador.",
    invalid_token: "Sessão inválida. Faça login novamente.",
    invalid_registration: "Sessão inválida. Faça login novamente.",
  };

  const handleForceLogout = useCallback(({ reason }) => {
    const message = FORCE_LOGOUT_MESSAGES[reason] || "Sua sessão foi encerrada. Faça login novamente.";
    toast.warning(message);

    if (reason === 'already_logged_in') {
      // Não derruba a sessão local, apenas mostra o aviso
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    logout();
  }, [logout]);

  const apiUrl = process.env.REACT_APP_URL_API_V1?.replace('/api/v1/web', '');

  const isPublicRoute = publicRoutes.some(route =>
    typeof route === "string"
      ? route === location.pathname
      : route instanceof RegExp && route.test(location.pathname)
  );

  useEffect(() => {
    const shouldConnect = user && !isPublicRoute;

    if (!user?.id || isPublicRoute) return;

    if (shouldConnect && !socketRef.current) {
      console.log("✅ Conectando socket...");
      const socket = io(apiUrl, {
        query: {
          userId: user.id,
          companyUserId: user?.companyUser?.id,
        },
      });

      socket.on("connect", () => {
        console.log("🔌 WebSocket conectado");
        const token = getAccessToken();
        socket.emit("register", { userId: user.id, token, companyUserId: user?.companyUser?.id });
      });

      socket.on("disconnect", () => {
        console.log("🔌 WebSocket desconectado");
      });

      socket.on("force-logout", handleForceLogout);

      socketRef.current = socket;
    }

    if (!user?.id && socketRef.current) {
      console.log("❌ Desconectando socket...");
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        console.log("🔌 WebSocket desconectado ao desmontar");
      }
    };
  }, [user?.id, handleForceLogout, location.pathname]);

  return (
    <SocketContext.Provider
      value={socketRef.current}
    >
      {children}
    </SocketContext.Provider>
  );
}
